import type { CountdownConfig } from './templates';

function storageKey(formId: string): string {
  return `cms_lf_countdown_${formId}`;
}

/** 'fixed' mode shares one deadline across every visitor; 'evergreen' starts counting down from
 * this visitor's own first impression, persisted the same way frequencyCap.ts persists suppression
 * state so a page refresh doesn't reset their timer. Null means "nothing to show" — either mode is
 * misconfigured (no deadline / no duration) or storage is unavailable and it degrades further. */
function resolveDeadline(formId: string, countdown: CountdownConfig): number | null {
  if (countdown.mode === 'fixed') {
    if (!countdown.deadline) return null;
    const parsed = new Date(countdown.deadline).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }

  const durationMs = Math.max(countdown.durationMinutes ?? 0, 0) * 60 * 1000;
  if (durationMs <= 0) return null;

  try {
    const key = storageKey(formId);
    const stored = localStorage.getItem(key);
    const startedAt = stored ? Number(stored) : Date.now();
    if (!stored) localStorage.setItem(key, String(startedAt));
    return startedAt + durationMs;
  } catch {
    // No storage — still shows a timer, it just restarts every page load instead of persisting.
    return Date.now() + durationMs;
  }
}

function format(msRemaining: number): string {
  const totalSeconds = Math.max(Math.floor(msRemaining / 1000), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

function applyExpiry(countdown: CountdownConfig, panelRoot: HTMLElement): void {
  switch (countdown.expireAction) {
    case 'redirect':
      if (countdown.redirectUrl) window.location.href = countdown.redirectUrl;
      return;
    case 'hide-form':
      panelRoot.style.display = 'none';
      return;
    case 'hide-cta':
    default: {
      const submit = panelRoot.querySelector<HTMLButtonElement>('.submit');
      if (submit) {
        submit.disabled = true;
        submit.textContent = "Time's up";
      }
    }
  }
}

/** Renders a ticking `HH:MM:SS` (or `MM:SS` under an hour) element and wires up its expiry
 * behavior against `panelRoot` — the renderer's own outer panel element, used for `hide-form` and
 * to find the `.submit` button for `hide-cta`. Returns null when there's no usable countdown
 * config, so callers can unconditionally do `container.append(buildCountdown(...) ?? [])`-style
 * composition without a config check of their own. */
export function buildCountdown(countdown: CountdownConfig | undefined, formId: string, panelRoot: HTMLElement): HTMLElement | null {
  if (!countdown) return null;
  const deadline = resolveDeadline(formId, countdown);
  if (deadline === null) return null;

  const el = document.createElement('div');
  el.className = 'countdown';

  if (countdown.label) {
    const label = document.createElement('span');
    label.className = 'countdown-label';
    label.textContent = countdown.label;
    el.appendChild(label);
  }

  const clock = document.createElement('span');
  clock.className = 'countdown-clock';
  el.appendChild(clock);

  const tick = () => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      clock.textContent = format(0);
      window.clearInterval(intervalId);
      applyExpiry(countdown, panelRoot);
      return;
    }
    clock.textContent = format(remaining);
  };

  tick();
  const intervalId = window.setInterval(tick, 1000);
  return el;
}
